// SEO utilities for generating meta tags and structured data
import { platforms } from "@/data/products";

const SITE_URL = "https://mehubarim.co.il";
const SITE_NAME = "מחוברים";
const DEFAULT_IMAGE = `${SITE_URL}/images/home/hero-social-media-promotion.jpg`;

export const seoData = {
  home: {
    title: "מחוברים | קידום ברשתות חברתיות - עוקבים, לייקים, צפיות",
    description: "מחוברים - שירות קידום מקצועי ברשתות חברתיות. קנו עוקבים, לייקים, צפיות ותגובות באינסטגרם, יוטיוב, טיקטוק, טוויטר ועוד. משלוח מהיר, תשלום מאובטח.",
    canonical: SITE_URL,
  },
  reviews: {
    title: "ביקורות לקוחות | מחוברים - חוות דעת אמיתיות",
    description: "קראו ביקורות וחוות דעת אמיתיות מלקוחות מרוצים של מחוברים. שירות קידום ברשתות חברתיות עם דירוג 4.8 כוכבים.",
    canonical: `${SITE_URL}/reviews`,
  },
  blog: {
    title: "בלוג מחוברים | מדריכים וטיפים לקידום ברשתות חברתיות",
    description: "מדריכים מקצועיים, כתבות וטיפים לקידום ברשתות חברתיות. למדו איך להגדיל עוקבים, לייקים וצפיות בצורה חכמה.",
    canonical: `${SITE_URL}/blog`,
  },
  checkout: {
    title: "תשלום | מחוברים - השלמת הזמנה",
    description: "השלימו את ההזמנה שלכם במחוברים. תשלום מאובטח בכרטיס אשראי או מטבע דיגיטלי.",
    canonical: `${SITE_URL}/checkout`,
  },
  cookiePolicy: {
    title: "מדיניות עוגיות | מחוברים",
    description: "מדיניות העוגיות של אתר מחוברים. מידע על השימוש בעוגיות ובטכנולוגיות מעקב באתר.",
    canonical: `${SITE_URL}/cookie-policy`,
  },
};

export function getPlatformSeo(platformSlug: string) {
  const platform = platforms.find((p) => p.slug === platformSlug);
  if (!platform) return null;
  return {
    title: `קנה ${platform.name} - עוקבים, לייקים וצפיות | מחוברים`,
    description: `שירותי קידום ל${platform.name}: עוקבים, לייקים, צפיות ותגובות. משלוח מהיר, מחירים אטרקטיביים ואחריות על כל הזמנה. מחוברים - הבחירה המקצועית.`,
    canonical: `${SITE_URL}/${platformSlug}`,
    platformName: platform.name,
  };
}

export function getProductSeo(platformSlug: string, productSlug: string) {
  const platform = platforms.find((p) => p.slug === platformSlug);
  if (!platform) return null;
  const product = platform.products.find((p) => p.slug === productSlug);
  if (!product) return null;
  return {
    title: `${product.name} | ${platform.name} - מחוברים`,
    description: `קנו ${product.name} ב${platform.name} במחיר ${product.price} ל-${product.perUnit}. משלוח מהיר, שירות אמין ותמיכה 24/7. מחוברים - קידום מקצועי ברשתות חברתיות.`,
    canonical: `${SITE_URL}/${platformSlug}/${productSlug}`,
    price: product.price,
    productName: product.name,
    platformName: platform.name,
  };
}

export function generateWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    description: seoData.home.description,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/?search={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/images/home/mehubarim-logo.webp`,
    contactPoint: {
      "@type": "ContactPoint",
      email: "support@mehubarim.co.il",
      contactType: "customer service",
      availableLanguage: ["Hebrew", "English", "Russian"],
    },
    sameAs: [],
  };
}

export function generateProductSchema(platformSlug: string, productSlug: string) {
  const platform = platforms.find((p) => p.slug === platformSlug);
  if (!platform) return null;
  const product = platform.products.find((p) => p.slug === productSlug);
  if (!product) return null;

  const priceNum = parseFloat(product.price.replace(/[^\d.]/g, ""));

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: `${product.name} - שירות קידום ל${platform.name} מבית מחוברים`,
    image: `${SITE_URL}${product.image}`,
    brand: { "@type": "Brand", name: SITE_NAME },
    offers: {
      "@type": "Offer",
      price: priceNum,
      priceCurrency: "ILS",
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}/${platformSlug}/${productSlug}`,
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "IL",
        returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
        merchantReturnDays: 0,
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value: 0,
          currency: "ILS",
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: "IL",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: 0,
            maxValue: 1,
            unitCode: "HUR",
          },
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: 0,
            maxValue: 24,
            unitCode: "HUR",
          },
        },
      },
    },
  };
}

export function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function generateArticleSchema(post: { title: string; excerpt: string; slug: string; created_at: string; image_url?: string | null }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.created_at,
    author: { "@type": "Organization", name: SITE_NAME },
    publisher: { "@type": "Organization", name: SITE_NAME, logo: { "@type": "ImageObject", url: `${SITE_URL}/images/home/mehubarim-logo.webp` } },
    image: post.image_url || DEFAULT_IMAGE,
    mainEntityOfPage: `${SITE_URL}/blog/post/${post.slug}`,
  };
}

export { SITE_URL, SITE_NAME, DEFAULT_IMAGE };
